import { Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { SectionHeader } from "@/components/SectionComponents";

const plans = [
  {
    name: "Starter",
    monthlyPrice: "Free",
    yearlyPrice: "Free",
    description: "For small teams getting started",
    users: "Up to 15 users",
    highlight: false,
    features: ["All Lingua2 basic features"],
    cta: "Contact Sales",
  },
  {
    name: "Enterprise",
    monthlyPrice: null,
    description: "Custom solutions for large organizations at any scale.",
    users: "Unlimited users",
    highlight: false,
    features: ["All Lingua2 basic features", "Find partners from your organization worldwide", "Dedicated monitoring of users", "Dedicated support"],
    cta: "Contact Sales",
  },
  {
    name: "Professional",
    monthlyPrice: 290,
    yearlyPrice: 249,
    description: "For growing organizations with international teams.",
    users: "Up to 50 users",
    highlight: true,
    badge: "Most Popular",
    features: ["All Lingua2 basic features", "Find partners from your organization worldwide"],
    cta: "Contact Sales",
  },
  
];

export default function Pricing() {
  const [annual, setAnnual] = useState(true);

  return (
    <div className="min-h-screen bg-background">

      <section className="pt-28 pb-12 bg-surface border-b border-border">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">Pricing</p>
          <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground leading-tight mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-base text-muted-foreground mb-8">
            Find the plan that best swits your organization
          </p>
          <div className="flex items-center justify-center gap-3">
            <span className={`text-sm font-medium ${!annual ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} className="data-[state=checked]:bg-primary" />
            <span className={`text-sm font-medium ${annual ? "text-foreground" : "text-muted-foreground"}`}>Annual</span>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary">Save</span>
          </div>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`flex flex-col rounded-2xl border p-8 relative ${
                  plan.highlight
                    ? "border-primary bg-primary shadow-primary"
                    : "border-border bg-card shadow-md-token"
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-foreground text-background">
                      {plan.badge}
                    </span>
                  </div>
                )}
                <div className="mb-6">
                  <h3 className={`text-xl font-display font-bold mb-1 ${plan.highlight ? "text-primary-foreground" : "text-foreground"}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm mb-4 leading-relaxed ${plan.highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {plan.description}
                  </p>
                  {plan.monthlyPrice ? (
                    <div className="flex items-baseline gap-1">
                      <span className={`text-4xl font-display font-bold ${plan.highlight ? "text-primary-foreground" : "text-foreground"}`}>
                        &euro;{annual ? plan.yearlyPrice : plan.monthlyPrice}
                      </span>
                      <span className={`text-sm ${plan.highlight ? "text-primary-foreground/60" : "text-muted-foreground"}`}>/mo</span>
                    </div>
                  ) : (
                    <div className={`text-3xl font-display font-bold ${plan.highlight ? "text-primary-foreground" : "text-foreground"}`}>Custom</div>
                  )}
                  <p className={`text-xs mt-1 ${plan.highlight ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                    {plan.users} &middot; {annual && plan.monthlyPrice ? "billed annually" : plan.monthlyPrice ? "billed monthly" : "contact for pricing"}
                  </p>
                </div>

                <ul className="space-y-2.5 mb-8 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${plan.highlight ? "text-primary-foreground" : "text-primary"}`} />
                      <span className={`text-sm ${plan.highlight ? "text-primary-foreground/85" : "text-foreground/80"}`}>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  size="lg"
                  asChild
                  className={`w-full mt-auto font-semibold transition-fast ${
                    plan.highlight
                      ? "bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                      : "bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary"
                  }`}
                >
                  <Link to="/contact">{plan.cta} <ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 rounded-xl border border-border bg-card text-center">
            <p className="text-sm font-semibold text-foreground mb-1">Need a custom plan?</p>
            <p className="text-sm text-muted-foreground mb-4">We build tailored solutions for universities, governments, and large enterprises.</p>
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/5 transition-fast">
              <Link to="/contact">Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
